import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { createQaChunk, deleteQaChunk, getQaChunk, getQaChunks, importQaChunk, updateQaChunk } from './api';
import { queryClient } from 'providers/AllProviders';
import { QaChunkRequest, QaChunkResponse } from './types';

export function useGetQaChunks(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: QaChunkResponse[]; meta: MetaApiResponse }, Error>
) {
  const { workspaceId, agentId } = useParams();

  return useQuery<{ data: QaChunkResponse[]; meta: MetaApiResponse }>({
    queryKey: ['qaChunks', workspaceId, agentId, params],
    queryFn: () => getQaChunks(workspaceId!, agentId!, params),
    enabled: Boolean(agentId && workspaceId),
    ...options
  });
}

export function useGetQaChunk(options?: UseQueryOptions<QaChunkResponse, Error>) {
  const { workspaceId, agentId, chunkId } = useParams();

  return useQuery<QaChunkResponse>({
    queryKey: ['qaChunk', workspaceId, agentId, chunkId],
    queryFn: () => getQaChunk(workspaceId!, agentId!, chunkId!),
    enabled: Boolean(agentId && workspaceId && chunkId),
    ...options
  });
}

export function useCreateOrUpdateQaChunk() {
  const navigate = useNavigate();
  const { workspaceId, agentId, chunkId } = useParams<{
    workspaceId: string;
    agentId: string;
    chunkId: string;
  }>();

  return useMutation({
    mutationFn: (payload: QaChunkRequest) =>
      chunkId
        ? updateQaChunk(workspaceId!, agentId!, chunkId!, payload)
        : createQaChunk(workspaceId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['qaChunks', workspaceId, agentId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId, agentId],
        refetchType: 'active'
      });
      if (chunkId) {
        queryClient.invalidateQueries({
          queryKey: ['qaChunks', workspaceId, agentId, chunkId],
          refetchType: 'active'
        });
      }
      navigate(-1);
    }
  });
}

export function useDeleteQaChunk() {
  const { workspaceId, agentId } = useParams<{ workspaceId: string; agentId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteQaChunk(workspaceId!, agentId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['qaChunks'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', workspaceId, agentId],
        refetchType: 'active'
      });
    }
  });
}

export function useImportQaChunk() {
  const { workspaceId, agentId } = useParams<{ workspaceId: string; agentId: string }>();

  return useMutation({
    mutationFn: (payload: FormData) => importQaChunk(workspaceId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['s'],
        refetchType: 'active'
      });
    }
  });
}
